import type {
  AiConfig,
  AiDraftResponse,
  AiIntent,
  AiSettings,
  Bootstrap,
  CannedInput,
  CannedItem,
  CannedList,
  ConversationDetail,
  ConversationPage,
  ConversationPatch,
  ConversationSummary,
  LearnAction,
  LearnItem,
  ListQuery,
  MediaAsset,
  MediaList,
  MediaPatch,
  MediaQuery,
  MediaRegister,
  MediaUploadRequest,
  MediaUploadTicket,
  MessagePage,
  OrderDraft,
  SearchResult,
  SendMessageBody,
  SendResult,
  UploadTarget,
  WebchatSettings,
  WebchatSettingsResponse,
  WebOrders,
  WebVisitorInfo,
} from './types'

/** Progress of one file upload (bytes sent so far / total). */
export type UploadProgress = (sent: number, total: number) => void

/** Everything the UI asks of the backend. Implemented by `MockChatApi` and `HttpChatApi`. */
export interface ChatApi {
  bootstrap(): Promise<Bootstrap>
  listConversations(q: ListQuery): Promise<ConversationPage>
  getConversation(id: string): Promise<ConversationDetail>
  listMessages(id: string, opts?: { before?: string | null; limit?: number }): Promise<MessagePage>
  search(q: string): Promise<SearchResult>
  // ---- phase 2 (writes) — see docs/api-contract.md "เขียน"
  markRead(id: string): Promise<void>
  sendMessage(id: string, body: SendMessageBody): Promise<SendResult>
  /** Put a failed message back in the send queue (POST /messages/:id/retry). */
  retryMessage(messageId: string): Promise<SendResult>
  updateConversation(id: string, patch: ConversationPatch): Promise<ConversationSummary>
  setAvailability(available: boolean): Promise<void>
  // ---- not in v1 (POS order creation undecided): mock only, http returns null
  getOrderDraft(id: string): Promise<OrderDraft | null>
  // ---- "/ln" card in the chat (api/app/ai/learn_routes.py): same role rules as /knowledge
  /** owner: approve (optionally as edited) · owner: reject ("ไม่ใช้") */
  learnAction(messageId: string, action: LearnAction, text?: string): Promise<{ item: LearnItem }>
  /** owner / head: change the proposed rule text (head: still waits for the owner) */
  learnEdit(messageId: string, text: string): Promise<{ item: LearnItem }>
  // ---- AI (docs/api-contract.md "AI"): draft + 2 canned + side-panel data for the newest customer message
  /** Read only (round 9): the draft already made for the latest customer message, or `idle`. No AI work. */
  getDraft(id: string, opts?: { refresh?: boolean }): Promise<AiDraftResponse>
  /** The staff pressed the draft box: draft now (the existing one for this message if any) · `refresh` = a new run ·
   *  `suggestionId` = "เรียบเรียงใหม่" of that script suggestion (api/script, docs/script.md) · with `adapt` =
   *  "ปรับตามแนวลูกค้า": re-toned for the customer's style, its facts locked. */
  requestDraft(id: string, opts?: { refresh?: boolean; suggestionId?: string | null; adapt?: boolean; nextStep?: boolean }): Promise<AiDraftResponse>
  // ---- library (docs/library.md): image/video library + answer library
  listMedia(q?: MediaQuery): Promise<MediaList>
  /** Ask for upload tickets (or get the identical file already stored). */
  createMediaUpload(req: MediaUploadRequest): Promise<MediaUploadTicket>
  /** PUT a file (or its preview) straight to the Worker with a ticket; big files go up in parts. */
  putMediaObject(
    target: UploadTarget & { multipart?: boolean; part_bytes?: number },
    blob: Blob,
    opts?: { onProgress?: UploadProgress; signal?: AbortSignal },
  ): Promise<void>
  registerMedia(body: MediaRegister): Promise<MediaAsset>
  updateMedia(id: string, patch: MediaPatch): Promise<MediaAsset>
  deleteMedia(id: string): Promise<void>
  listCanned(opts?: { conversationId?: string | null }): Promise<CannedList>
  createCanned(body: CannedInput): Promise<CannedItem>
  updateCanned(id: string, patch: CannedInput): Promise<CannedItem>
  deleteCanned(id: string): Promise<void>
  reorderCanned(ids: string[]): Promise<void>
  listIntents(): Promise<AiIntent[]>
  // ---- customer profile (name + picture from the platform): a picture failed to load → fetch it again soon
  refreshProfile(contactId: string): Promise<void>
  // ---- AI settings page + slip check (round 6, api/app/ai/routes.py · api/app/slips.py)
  getAiConfig(): Promise<AiConfig>
  /** Owner only. Returns the saved settings. */
  updateAiSettings(patch: Partial<AiSettings>): Promise<AiSettings>
  /** Owner only: check every new customer image automatically (costs money per image). */
  updateSlipSettings(patch: { auto: boolean }): Promise<{ auto: boolean }>
  /** Staff: check a customer image as a payment slip (never replies to the customer). */
  checkSlip(messageId: string): Promise<SendResult>
  // ---- web chat (docs/webchat.md): visitor card in the side panel, "typing" to the visitor, settings
  /** null when the chat is not a web chat. */
  getWebVisitor(id: string): Promise<WebVisitorInfo | null>
  /** POS orders of the phone the web visitor typed / logged in with (null when not a web chat). */
  getWebOrders(id: string, fresh?: boolean): Promise<WebOrders | null>
  sendTyping(id: string): Promise<void>
  getWebchatSettings(): Promise<WebchatSettingsResponse>
  updateWebchatSettings(patch: Partial<WebchatSettings>): Promise<WebchatSettingsResponse>
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}
